"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReportService = void 0;
const tslib_1 = require("tslib");
const groupBy_1 = tslib_1.__importDefault(require("lodash/groupBy"));
const Enums_1 = require("../../PredefinedConfig/Common/Enums");
const GeneralConstants_1 = require("../Constants/GeneralConstants");
const ArrayExtensions_1 = tslib_1.__importDefault(require("../Extensions/ArrayExtensions"));
const FormatHelper_1 = tslib_1.__importStar(require("../Helpers/FormatHelper"));
const StringExtensions_1 = tslib_1.__importDefault(require("../Extensions/StringExtensions"));
const ModuleConstants_1 = require("../Constants/ModuleConstants");
const uuid_1 = require("../../components/utils/uuid");
const ObjectFactory_1 = require("../ObjectFactory");
class ReportService {
    constructor(adaptableApi) {
        this.adaptableApi = adaptableApi;
        this.excelStylesCache = {};
        this.cellClassKey2excelStyleIdMap = {};
        this.excelStylesWithFormattedDate = {};
        this.adaptableApi = adaptableApi;
    }
    resetExcelStyleMemoization() {
        this.excelStylesCache = {};
        this.cellClassKey2excelStyleIdMap = {};
        this.excelStylesWithFormattedDate = {};
    }
    registerExcelStyle(excelStyle, cellClassKey) {
        const excelStyleKey = JSON.stringify(excelStyle);
        if (!this.excelStylesCache[excelStyleKey]) {
            const excelStyleId = (0, uuid_1.createUuid)();
            const excelStyleWithId = Object.assign(Object.assign({}, excelStyle), { id: excelStyleId });
            this.excelStylesCache[excelStyleKey] = excelStyleWithId;
        }
        this.cellClassKey2excelStyleIdMap[cellClassKey] = this.excelStylesCache[excelStyleKey].id;
    }
    getRegisteredExcelStyles() {
        return Object.values(this.excelStylesCache);
    }
    getExcelStyleIdForCellClassKey(cellClassKey) {
        return this.cellClassKey2excelStyleIdMap[cellClassKey];
    }
    registerExcelStyleWithFormattedDate(cellClassId, isoFormattedValue) {
        this.excelStylesWithFormattedDate[cellClassId] = isoFormattedValue;
    }
    getExcelStyleWithFormattedDate(cellClassId) {
        return this.excelStylesWithFormattedDate[cellClassId];
    }
    CreateSystemReport(systemReportName) {
        switch (systemReportName) {
            case GeneralConstants_1.VISUAL_DATA_REPORT:
                return {
                    Uuid: (0, uuid_1.createUuid)(),
                    Name: GeneralConstants_1.VISUAL_DATA_REPORT,
                    ReportColumnScope: 'VisibleColumns',
                    ReportRowScope: 'VisibleRows',
                    Query: undefined,
                    IsReadOnly: true,
                };
            case GeneralConstants_1.ALL_DATA_REPORT:
                return {
                    Uuid: (0, uuid_1.createUuid)(),
                    Name: GeneralConstants_1.ALL_DATA_REPORT,
                    ReportColumnScope: 'AllColumns',
                    ReportRowScope: 'AllRows',
                    Query: undefined,
                    IsReadOnly: true,
                };
            case GeneralConstants_1.CURRENT_DATA_REPORT:
                return {
                    Uuid: (0, uuid_1.createUuid)(),
                    Name: GeneralConstants_1.CURRENT_DATA_REPORT,
                    ReportColumnScope: 'VisibleColumns',
                    ReportRowScope: 'VisibleRows',
                    Query: undefined,
                    IsReadOnly: true,
                };
            case GeneralConstants_1.SELECTED_CELLS_REPORT:
                return {
                    Uuid: (0, uuid_1.createUuid)(),
                    Name: GeneralConstants_1.SELECTED_CELLS_REPORT,
                    ReportColumnScope: 'SelectedColumns',
                    ReportRowScope: 'SelectedCellRows',
                    Query: undefined,
                    IsReadOnly: true,
                };
            case GeneralConstants_1.SELECTED_ROWS_REPORT:
                return {
                    Uuid: (0, uuid_1.createUuid)(),
                    Name: GeneralConstants_1.SELECTED_ROWS_REPORT,
                    ReportColumnScope: 'VisibleColumns',
                    ReportRowScope: 'SelectedRows',
                    Query: undefined,
                    IsReadOnly: true,
                };
        }
    }
    IsSystemReport(reportName) {
        return (reportName == null ||
            reportName == GeneralConstants_1.ALL_DATA_REPORT ||
            reportName == GeneralConstants_1.CURRENT_DATA_REPORT ||
            reportName == GeneralConstants_1.SELECTED_CELLS_REPORT ||
            reportName == GeneralConstants_1.SELECTED_ROWS_REPORT ||
            reportName == GeneralConstants_1.VISUAL_DATA_REPORT);
    }
    IsSystemReportActive(reportName) {
        if (reportName == GeneralConstants_1.SELECTED_CELLS_REPORT || reportName == GeneralConstants_1.SELECTED_ROWS_REPORT) {
            return this.adaptableApi.gridApi.isGridSelectable();
        }
        if (reportName === GeneralConstants_1.VISUAL_DATA_REPORT) {
            return this.adaptableApi.exportApi
                .getAvailableExportDestinations()
                .includes(Enums_1.ExportDestination.Excel);
        }
        return true;
    }
    IsSystemExportDestinationActive(exportDestination) {
        if (exportDestination === Enums_1.ExportDestination.Excel) {
            return this.adaptableApi.exportApi.canExportToExcel();
        }
        return true;
    }
    GetReportColumnScopeShortDescription(report) {
        var _a, _b;
        if (this.adaptableApi.exportApi.isExternalReport(report)) {
            return ['[Custom Columns]'];
        }
        switch (report.ReportColumnScope) {
            case 'AllColumns':
                return ['[All Columns]'];
            case 'VisibleColumns':
                return ['[Visible Columns]'];
            case 'SelectedColumns':
                return ['[Selected Columns]'];
            case 'ScopeColumns':
                if ('ColumnIds' in (report === null || report === void 0 ? void 0 : report.Scope)) {
                    return (_b = (_a = report.Scope.ColumnIds).map) === null || _b === void 0 ? void 0 : _b.call(_a, (columnId) => { var _a; return (_a = this.adaptableApi.columnApi.getFriendlyNameForColumnId(columnId)) !== null && _a !== void 0 ? _a : columnId; });
                }
                return ['[Bespoke Columns]'];
        }
    }
    GetReportColumnScopeLongDescription(report) {
        if (this.adaptableApi.exportApi.isExternalReport(report)) {
            return '[Custom Columns]';
        }
        switch (report.ReportColumnScope) {
            case 'AllColumns':
                return '[All Columns]';
            case 'VisibleColumns':
                return '[Visible Columns]';
            case 'SelectedColumns':
                return '[Selected Columns]';
            case 'ScopeColumns':
                return this.adaptableApi.scopeApi.getScopeDescription(report.Scope);
        }
    }
    GetReportExpressionDescription(report, cols) {
        if (this.adaptableApi.exportApi.isExternalReport(report)) {
            return '[Custom Data]';
        }
        if (this.IsSystemReport(report.Name)) {
            if (report.Name == GeneralConstants_1.ALL_DATA_REPORT) {
                return '[All Data]';
            }
            else if (report.Name == GeneralConstants_1.CURRENT_DATA_REPORT) {
                return '[All Current Data]';
            }
            else if (report.Name == GeneralConstants_1.SELECTED_CELLS_REPORT) {
                return '[Selected Cells Data]';
            }
            else if (report.Name == GeneralConstants_1.SELECTED_ROWS_REPORT) {
                return '[Selected Rows Data]';
            }
            else if (report.Name == GeneralConstants_1.VISUAL_DATA_REPORT) {
                return '[Current Data as WYSIWYG]';
            }
        }
        else {
            switch (report.ReportRowScope) {
                case 'AllRows':
                    return '[All Rows]';
                case 'VisibleRows':
                    return '[Visible Rows]';
                case 'SelectedRows':
                    return '[Selected Rows]';
                case 'ExpressionRows':
                    return this.adaptableApi.internalApi.getAdaptableQueryExpressionText(report.Query);
            }
        }
    }
    getReportDataColumns(report, includePrimaryKey = false) {
        let reportColumns = [];
        let gridColumns = this.adaptableApi.columnApi.getExportableColumns();
        if (this.adaptableApi.exportApi.isExternalReport(report)) {
            return reportColumns;
        }
        // first get the cols depending on the Column Scope
        switch (report.ReportColumnScope) {
            case 'AllColumns':
                reportColumns = gridColumns;
                break;
            case 'VisibleColumns':
                reportColumns = gridColumns.filter((c) => c.visible);
                break;
            case 'SelectedColumns':
                // we extract the selected columns from the grid columns to preserve the grid column order
                const selectedColumnIds = this.adaptableApi.gridApi
                    .getSelectedCellInfo()
                    .columns.map((column) => column.columnId);
                reportColumns = gridColumns.filter((gridColumn) => selectedColumnIds.includes(gridColumn.columnId));
                break;
            case 'ScopeColumns':
                if ('ColumnIds' in report.Scope) {
                    reportColumns = report.Scope.ColumnIds.map((columnId) => this.adaptableApi.columnApi.getColumnWithColumnId(columnId)).filter((c) => c);
                }
                else {
                    reportColumns = this.adaptableApi.scopeApi.getColumnsForScope(report.Scope);
                }
                break;
        }
        if (includePrimaryKey) {
            const pkColumn = reportColumns.find((column) => column.columnId === this.adaptableApi.optionsApi.getPrimaryKey());
            // TODO simplify after we fix the IsPrimaryKey bug
            // const pkColumn = reportColumns.find(column => column.IsPrimaryKey);
            if (!pkColumn && !!this.adaptableApi.columnApi.getPrimaryKeyColumn()) {
                reportColumns.push(this.adaptableApi.columnApi.getPrimaryKeyColumn());
            }
        }
        return reportColumns.map((column) => {
            var _a;
            return ({
                columnId: column.columnId,
                friendlyName: column.friendlyName,
                dataType: column.dataType,
                field: (_a = column.field) !== null && _a !== void 0 ? _a : column.columnId,
            });
        });
    }
    getReportDataRows(report, columns, includePrimaryKey) {
        var _a, _b;
        if (ArrayExtensions_1.default.IsNullOrEmpty(columns)) {
            return [];
        }
        const columnIds = columns.map((column) => column.columnId);
        const resultRowData = [];
        switch (report.ReportRowScope) {
            case 'AllRows':
                this.adaptableApi.internalApi.forAllRowNodesDo((rowNode) => {
                    resultRowData.push(this.getRowObjectForColumnIds(rowNode, columnIds));
                });
                break;
            case 'VisibleRows':
                this.adaptableApi.internalApi.forAllVisibleRowNodesDo((rowNode) => {
                    resultRowData.push(this.getRowObjectForColumnIds(rowNode, columnIds));
                });
                break;
            case 'ExpressionRows':
                this.adaptableApi.internalApi.forAllRowNodesDo((rowNode) => {
                    var _a;
                    if (this.adaptableApi.internalApi
                        .getQueryLanguageService()
                        .evaluateBooleanExpression((_a = report.Query) === null || _a === void 0 ? void 0 : _a.BooleanExpression, ModuleConstants_1.ExportModuleId, rowNode)) {
                        resultRowData.push(this.getRowObjectForColumnIds(rowNode, columnIds));
                    }
                });
                break;
            case 'SelectedCellRows':
                const selectedCellInfo = this.adaptableApi.gridApi.getSelectedCellInfo();
                const { gridCells: GridCells } = selectedCellInfo;
                let selectedCellsByPrimaryKey = (0, groupBy_1.default)(GridCells, 'primaryKeyValue');
                // we iterate over all visibleRowNodes to preserve the current order
                this.adaptableApi.internalApi.forAllVisibleRowNodesDo((rowNode) => {
                    const rowPrimaryKeyValue = this.adaptableApi.gridApi.getPrimaryKeyValueForRowNode(rowNode);
                    const selectedRowCells = selectedCellsByPrimaryKey[rowPrimaryKeyValue];
                    if (selectedRowCells) {
                        const selectedRowColumnIds = selectedRowCells.map((rowCell) => rowCell.column.columnId);
                        const selectedColumnIds = columnIds.filter((columnId) => selectedRowColumnIds.includes(columnId));
                        const row = this.getRowObjectForColumnIds(rowNode, selectedColumnIds);
                        if (includePrimaryKey) {
                            row[this.adaptableApi.optionsApi.getPrimaryKey()] = rowPrimaryKeyValue;
                        }
                        resultRowData.push(row);
                    }
                });
                break;
            case 'SelectedRows':
                const selectedRowInfo = this.adaptableApi.gridApi.getSelectedRowInfo();
                const selectedGridRowPrimaryKeys = (_b = (_a = selectedRowInfo === null || selectedRowInfo === void 0 ? void 0 : selectedRowInfo.gridRows) === null || _a === void 0 ? void 0 : _a.filter((gr) => gr.rowInfo.isGroup == false).map((gridRow) => gridRow.primaryKeyValue)) !== null && _b !== void 0 ? _b : [];
                if (selectedGridRowPrimaryKeys.length) {
                    this.adaptableApi.internalApi.forAllRowNodesDo((rowNode) => {
                        const rowPrimaryKeyValue = this.adaptableApi.gridApi.getPrimaryKeyValueForRowNode(rowNode);
                        if (selectedGridRowPrimaryKeys.includes(rowPrimaryKeyValue)) {
                            resultRowData.push(this.getRowObjectForColumnIds(rowNode, columnIds));
                        }
                    });
                }
                break;
        }
        return resultRowData;
    }
    getReportData(report, includePrimaryKey = false) {
        if (this.adaptableApi.exportApi.isExternalReport(report)) {
            return this.adaptableApi.exportApi.runExternalReport(report.Name);
        }
        const columns = this.getReportDataColumns(report, includePrimaryKey);
        const rows = this.getReportDataRows(report, columns, includePrimaryKey);
        return { columns, rows };
    }
    getReportDataAsArray(report, includePrimaryKey = false) {
        const reportData = this.getReportData(report, includePrimaryKey);
        return this.convertReportDataToArray(reportData);
    }
    convertReportDataToArray(reportData) {
        return [
            reportData.columns.map((column) => column.friendlyName),
            ...reportData.rows.map((row) => reportData.columns.map((column) => row[column.columnId])),
        ];
    }
    getRowObjectForColumnIds(rowNode, columnIds) {
        return columnIds.reduce((result, columnId) => {
            result[columnId] = this.getCellExportValueFromRowNode(rowNode, columnId);
            return result;
        }, {});
    }
    PublishLiveLiveDataChangedEvent(reportDestination, liveDataTrigger, liveReport) {
        const liveDataChangedInfo = {
            adaptableApi: this.adaptableApi,
            reportDestination: reportDestination,
            liveDataTrigger: liveDataTrigger,
            liveReport: liveReport,
            userName: this.adaptableApi.optionsApi.getUserName(),
            adaptableId: this.adaptableApi.optionsApi.getAdaptableId(),
        };
        this.adaptableApi.eventApi.emit('LiveDataChanged', liveDataChangedInfo);
    }
    getCellExportValueFromRowNode(rowNode, columnId) {
        return this.getCellExportValueFromRawValue(rowNode, this.adaptableApi.gridApi.getRawValueFromRowNode(rowNode, columnId), columnId);
    }
    getCellExportValueFromRawValue(rowNode, cellRawValue, columnId) {
        if (StringExtensions_1.default.IsNullOrEmpty(cellRawValue)) {
            return cellRawValue;
        }
        const columnType = this.adaptableApi.columnApi.getColumnDataTypeForColumnId(columnId);
        // if this is a date column and there is a custom export date format provided, that will take precedence
        if (columnType === 'Date' && !!this.getCustomExportDateFormat()) {
            const exportDateFormat = this.getCustomExportDateFormat();
            return FormatHelper_1.default.DateFormatter(cellRawValue, {
                Pattern: exportDateFormat,
            });
        }
        // otherwise check the general export format types
        let cellExportFormat = this.adaptableApi.exportApi.internalApi.getCellExportFormatType(columnType);
        return this.getCellExportValueFromRawValueByType(rowNode, cellRawValue, columnId, cellExportFormat);
    }
    getReportFileName(reportName, destination) {
        let fileName;
        const reportFilename = this.adaptableApi.optionsApi.getExportOptions().reportFilename;
        if (reportFilename) {
            const reportFileNameContext = Object.assign(Object.assign({}, (0, ObjectFactory_1.createBaseContext)(this.adaptableApi)), { reportName,
                destination });
            fileName = reportFilename(reportFileNameContext);
        }
        else {
            fileName = StringExtensions_1.default.ReplaceEmptySpacesWithUnderscore(reportName);
            if (this.adaptableApi.optionsApi.getExportOptions().appendFileTimestamp) {
                fileName = `${fileName}_${(0, FormatHelper_1.DateFormatter)(new Date(), {
                    Pattern: 'yyyyMMdd_HHmmss',
                })}`;
            }
        }
        return fileName;
    }
    destroy() {
        // TO DO
    }
    getCustomExportDateFormat() {
        return this.adaptableApi.optionsApi.getExportOptions().exportDateFormat;
    }
    getCellExportValueFromRawValueByType(rowNode, cellRawValue, columnId, 
    // default to rawValue if, for some reason, the configs provide invalid values
    type = 'rawValue') {
        return type === 'rawValue'
            ? cellRawValue
            : // type === formattedValue
                this.adaptableApi.gridApi.getDisplayValueFromRawValue(rowNode, columnId, cellRawValue);
    }
}
exports.ReportService = ReportService;
